// The LureoEditor WebGL template's page script (Assets/WebGLTemplates/LureoEditor/TemplateData/script.js) around the
// three.js runtime instead of Unity's loader: the URL and scene-id handling, the parent-origin allow-list, the
// requestJwt/setJwt gate and live token, rendererLoaded, rendererError, the canvas and screenshot answers, and the
// keyboard and pointer forwarding. Every post is a renderer-bridge message, exactly { type, ...fields }.

import { ALLOWED_PARENT_ORIGINS, isWholeNumber, RENDERER_ACCEPTS, type EditorSession } from "@lureo/editor-bridge";
import type { RendererToEditorMessage } from "@lureo/editor-contracts";

export interface HostElement {
  readonly style: { display: string; width: string; background: string };
  textContent: string | null;
}

export interface HostCanvas {
  readonly width: number;
  readonly height: number;
  toDataURL(type: string, quality: unknown): string;
}

export interface HostKeyEvent {
  readonly key: string;
  readonly code: string;
  readonly keyCode?: number;
  readonly which?: number;
  readonly repeat?: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  readonly fromReactParent?: boolean;
}

export interface HostMessageEvent {
  readonly source: unknown;
  readonly origin: string;
  readonly data: unknown;
}

export interface HostParent {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface HostPage {
  readonly href: () => string;
  readonly origin: string;
  readonly referrer: string;
  // window.parent when it is not the window itself.
  readonly parent: HostParent | null;
  replaceUrl(url: string): void;
  addMessageListener(listener: (event: HostMessageEvent) => void): void;
  addDocumentKeyListener(listener: (event: HostKeyEvent) => void): void;
  addDocumentPointerListener(type: "pointerdown" | "mousedown", listener: () => void): void;
  setTimeout(callback: () => void, milliseconds: number): void;
  // document.querySelector('canvas#unity-canvas') || document.querySelector('canvas'), looked up on every request.
  findCanvas(): HostCanvas | null;
  // window.lureoEditorJwt.
  exposeEditorJwt(read: () => string | null): void;
  // window.lureoEditorReadAfter.
  exposeEditorReadAfter(read: () => number | null): void;
  // window.lureoEditorReadHomeUntil.
  exposeEditorReadHomeUntil(read: () => number | null): void;
  readonly loadingBar: HostElement;
  readonly progressBarFull: HostElement;
  readonly warningBanner: HostElement;
}

export interface EditorInstance {
  // Draws the current frame so the canvas holds it for toDataURL in the same task (ADR-07).
  renderForReadback(): void;
}

export interface EditorLoader {
  // createUnityInstance(canvas, config, onProgress).
  createInstance(onProgress: (progress: number) => void): Promise<EditorInstance>;
}

export interface HostBridgeOptions {
  readonly productVersion: string;
  // The loader script: Unity's Build/Web.loader.js, here the runtime chunk, fetched only once the JWT arrived.
  readonly loadLoader: () => Promise<EditorLoader>;
  // Where the latest token, what the editor sent of the cache and rendererLoaded are kept for the runtime's /api/v1
  // reads.
  readonly session: EditorSession;
}

export const BOOTSTRAP_TIMEOUT_MS = 10000;
const NON_ERROR_BANNER_MS = 5000;

const FORWARDED_KEYS: ReadonlySet<string> = new Set([
  "Delete",
  "Backspace",
  "Escape",
  " ",
  "c",
  "C",
  "f",
  "F",
  "g",
  "G",
  "h",
  "H",
  "l",
  "L",
  "t",
  "T",
  "z",
  "Z",
  "y",
  "Y",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "?",
  "[",
  "]",
]);
const THEME_STEP_KEYS: ReadonlySet<string> = new Set(["[", "]"]);

export function looksLikeJwt(token: unknown): token is string {
  if (typeof token !== "string" || token.length < 16) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  return parts.every((part) => part.length > 0);
}

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null;

// JavaScript's `value || fallback`.
const or = (value: unknown, fallback: unknown): unknown => {
  if (value) return value;
  return fallback;
};

// window.lureoEditorReadAfter (as the template has it) or lureoEditorReadHomeUntil, read through so it is always the
// value kept.
export function defineKeptValue(
  target: object,
  name: "lureoEditorReadAfter" | "lureoEditorReadHomeUntil",
  read: () => number | null,
): void {
  Object.defineProperty(target, name, { get: read, configurable: true, enumerable: true });
}

export interface HostBridge {
  readonly sceneId: string | null;
  readonly parentOrigin: string;
  readonly bootstrapStarted: boolean;
  readonly jwtToken: string | null;
  readonly readAfter: number | null;
  readonly readHomeUntil: number | null;
}

export function startHostBridge(page: HostPage, options: HostBridgeOptions): HostBridge {
  const currentUrl = new URL(page.href());
  const params = currentUrl.searchParams;
  const fromQuery = params.get("sceneId");
  const sceneId = fromQuery !== null && fromQuery !== "" ? fromQuery : currentUrl.hash !== "" ? currentUrl.hash.substring(1) : null;
  const appVersion = options.productVersion === "" ? "1.0.0" : options.productVersion;

  if (sceneId !== null && sceneId !== "") {
    const prettyUrl = new URL(currentUrl.toString());
    prettyUrl.searchParams.delete("sceneId");
    prettyUrl.hash = "";
    if (!prettyUrl.pathname.endsWith(`/${sceneId}`)) prettyUrl.pathname = `/${sceneId}`;
    page.replaceUrl(prettyUrl.toString());
  }

  const parentOrigin = ((): string => {
    try {
      if (page.referrer !== "") return new URL(page.referrer).origin;
    } catch {
      // A referrer that does not parse falls back to the page's own origin.
    }
    return page.origin;
  })();

  const isAllowedOrigin = (event: HostMessageEvent): boolean => {
    if (event.origin === "" || event.origin === page.origin) return true;
    return event.origin === parentOrigin;
  };

  // PORT-DEVIATION: Assets/WebGLTemplates/LureoEditor/TemplateData/script.js:153,210 — the template's listeners check the origin alone; here they also require event.source === window.parent, as RendererBridge.jslib:52 does for every other message, so no other same-origin window can hand the renderer a token or take a screenshot (research 11 §6).
  const isFromParent = (event: HostMessageEvent): boolean =>
    page.parent !== null && event.source === page.parent && isAllowedOrigin(event);

  const isAllowedParent = (origin: string): boolean => {
    if (origin === "") return false;
    if (origin === page.origin) return true;
    return ALLOWED_PARENT_ORIGINS.includes(origin);
  };

  const postToParent = (message: RendererToEditorMessage): void => {
    page.parent?.postMessage(message, parentOrigin);
  };

  const { loadingBar, progressBarFull, warningBanner } = page;

  const showBanner = (message: string, type: string): void => {
    warningBanner.style.display = "block";
    warningBanner.textContent = message;
    warningBanner.style.background = type === "error" ? "#f66" : "rgba(0,0,0,0.7)";
    if (type !== "error") {
      page.setTimeout(() => {
        warningBanner.style.display = "none";
      }, NON_ERROR_BANNER_MS);
    }
  };

  loadingBar.style.display = "block";

  const state = {
    bootstrapStarted: false,
    jwtToken: null as string | null,
    readAfter: null as number | null,
    readHomeUntil: null as number | null,
  };
  const keepToken = (token: string): void => {
    state.jwtToken = token;
    options.session.setLatestJwt(token);
  };

  page.exposeEditorJwt(() => state.jwtToken);

  // The editor's readAfter and readHomeUntil, kept from page load as the token is: they come on rendererLoaded, which
  // can be before the renderer's bridge listens, so the bridge's Start takes what is kept here (keptReadAfter,
  // keptReadHomeUntil). The highest of each: a readAfter of 0 is kept too, as this load's readAfter has come.
  page.exposeEditorReadAfter(() => state.readAfter);
  page.exposeEditorReadHomeUntil(() => state.readHomeUntil);
  page.addMessageListener((event) => {
    if (!isFromParent(event) || !isRecord(event.data)) return;
    const { data } = event;
    if (data["type"] === "readAfter") {
      const cacheSeq = data["cacheSeq"];
      if (!isWholeNumber(cacheSeq) || (state.readAfter !== null && cacheSeq <= state.readAfter)) return;
      state.readAfter = cacheSeq;
      options.session.setLatestReadAfter(cacheSeq);
    } else if (data["type"] === "readHomeUntil") {
      const until = data["until"];
      if (!isWholeNumber(until) || (state.readHomeUntil !== null && until <= state.readHomeUntil)) return;
      state.readHomeUntil = until;
      options.session.setLatestReadHomeUntil(until);
    }
  });

  const showUnauthorized = (reason: string): void => {
    loadingBar.style.display = "none";
    showBanner(
      `Unauthorized — please sign in at lureo.app and open this scene from there.${reason === "" ? "" : ` (${reason})`}`,
      "error",
    );
    postToParent({ type: "rendererError", message: "unauthorized", reason });
  };

  const onLoaded = (instance: EditorInstance): void => {
    loadingBar.style.display = "none";

    // From now on the editor answers with readAfter, which the reads a region may answer wait for
    options.session.sentRendererLoaded();
    postToParent({ type: "rendererLoaded", version: appVersion, accepts: [...RENDERER_ACCEPTS] });

    page.addMessageListener((event) => {
      if (!isFromParent(event)) return;
      // @unity-quirk Assets/WebGLTemplates/LureoEditor/TemplateData/script.js:212 — `event.data.type` on a null or undefined message throws in the listener.
      if (event.data === null || event.data === undefined) throw new TypeError("Cannot read properties of null (reading 'type')");
      const data: Readonly<Record<string, unknown>> = isRecord(event.data) ? event.data : {};
      const requestId = data["requestId"] as string;

      if (data["type"] === "checkCanvasReady") {
        const canvas = page.findCanvas();
        postToParent({
          type: "canvasReadyResponse",
          requestId,
          ready: canvas !== null && canvas.width > 0 && canvas.height > 0,
        });
      }

      if (data["type"] === "requestScreenshot") {
        const canvas = page.findCanvas();
        if (canvas === null) {
          postToParent({ type: "screenshotResponse", requestId, success: false, error: "Canvas not found" });
          return;
        }
        try {
          const format = or(data["format"], "png");
          const quality = or(data["quality"], 0.9);
          // PORT-QUESTION: Assets/WebGLTemplates/LureoEditor/TemplateData/script.js:232-235 — whether Unity's canvas still holds the last frame when toDataURL runs outside a frame (preserveDrawingBuffer off) — ported as: the frame is drawn first, then read in the same task.
          instance.renderForReadback();
          const dataUrl = canvas.toDataURL(`image/${String(format)}`, quality);
          postToParent({ type: "screenshotResponse", requestId, success: true, dataUrl });
        } catch (error) {
          postToParent({
            type: "screenshotResponse",
            requestId,
            success: false,
            error: (error as { message?: unknown }).message as string,
          });
        }
      }
    });
  };

  // createUnityInstance rejects with the message itself; the runtime rejects with an Error carrying it.
  const onFailed = (reason: unknown): void => {
    const message = reason instanceof Error ? reason.message : String(reason);
    loadingBar.style.display = "none";
    showBanner(message, "error");
    postToParent({ type: "rendererError", message });
  };

  const loaderOnLoad = (loader: EditorLoader): void => {
    loader
      .createInstance((progress) => {
        progressBarFull.style.width = `${String(100 * progress)}%`;
      })
      .then(onLoaded)
      .catch(onFailed);
  };

  const loaderOnError = (): void => {
    showBanner("Failed to load Unity loader script.", "error");
    postToParent({ type: "rendererError", message: "Loader script failed to load" });
  };

  const startBootstrap = (token: string): void => {
    if (state.bootstrapStarted) return;
    state.bootstrapStarted = true;
    keepToken(token);
    options.loadLoader().then(loaderOnLoad, loaderOnError);
  };

  if (!isAllowedParent(parentOrigin)) {
    showUnauthorized("untrusted parent origin");
  } else {
    showBanner("Verifying authentication…", "info");

    page.addMessageListener((event) => {
      if (!isFromParent(event)) return;
      if (!isRecord(event.data) || event.data["type"] !== "setJwt") return;
      const token = event.data["token"];
      if (!looksLikeJwt(token)) return;
      if (state.bootstrapStarted) {
        keepToken(token);
        return;
      }
      warningBanner.style.display = "none";
      startBootstrap(token);
    });

    postToParent({ type: "requestJwt" });

    page.setTimeout(() => {
      if (state.bootstrapStarted) return;
      showUnauthorized("no token received");
    }, BOOTSTRAP_TIMEOUT_MS);
  }

  page.addDocumentKeyListener((event) => {
    if (event.fromReactParent === true) return;
    const isModifierShortcut = event.metaKey || event.ctrlKey;
    const isInterestingKey = isModifierShortcut || event.code === "Space" || FORWARDED_KEYS.has(event.key);
    if (!isInterestingKey) return;
    if (event.repeat === true && THEME_STEP_KEYS.has(event.key)) return;
    postToParent({
      type: "keyboardEvent",
      key: event.key,
      code: event.code,
      keyCode: or(event.keyCode, event.which) as number,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    });
  });

  const notifyPointerDown = (): void => {
    postToParent({ type: "pointerDown" });
  };
  page.addDocumentPointerListener("pointerdown", notifyPointerDown);
  page.addDocumentPointerListener("mousedown", notifyPointerDown);

  return {
    sceneId,
    parentOrigin,
    get bootstrapStarted() {
      return state.bootstrapStarted;
    },
    get jwtToken() {
      return state.jwtToken;
    },
    get readAfter() {
      return state.readAfter;
    },
    get readHomeUntil() {
      return state.readHomeUntil;
    },
  };
}
